import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Collapse,
  Badge,
  InputAdornment,
  Stack,
  Button,
  SwipeableDrawer,
  Typography,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import { useMobile } from '../hooks/useMobile';

/**
 * ResponsiveFilters — Responsive filter bar.
 * On desktop: shows all filters inline.
 * On mobile: shows only search bar + filter button that opens a bottom drawer with all filters.
 * 
 * Props:
 * - searchValue: string
 * - onSearchChange: (value) => void
 * - searchPlaceholder: string
 * - children: ReactNode — filter components (Selects, DatePickers, etc.)
 * - activeFilterCount: number — badge on filter button showing active filters
 * - onClearFilters: () => void — clear all filters
 */
const ResponsiveFilters = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Cerca...',
  children,
  activeFilterCount = 0,
  onClearFilters,
}) => {
  const { isMobile } = useMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Desktop view: all filters inline
  if (!isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {children}
      </Box>
    );
  }

  // Mobile view: search bar + filter drawer
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {children && (
          <IconButton
            onClick={() => setFiltersOpen(true)}
            color={activeFilterCount > 0 ? 'primary' : 'default'}
          >
            <Badge badgeContent={activeFilterCount} color="primary" variant="dot" invisible={activeFilterCount === 0}>
              <FilterListIcon />
            </Badge>
          </IconButton>
        )}
      </Box>

      {/* Filter bottom drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onOpen={() => setFiltersOpen(true)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '70vh',
            px: 2,
            pb: 3,
          },
        }}
      >
        {/* Drag handle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'grey.300' }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Filtri
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onClearFilters && activeFilterCount > 0 && (
              <Button size="small" onClick={onClearFilters}>
                Pulisci
              </Button>
            )}
            <IconButton size="small" onClick={() => setFiltersOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          {children}
        </Stack>

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 3 }}
          onClick={() => setFiltersOpen(false)}
        >
          Applica Filtri
        </Button>
      </SwipeableDrawer>
    </Box>
  );
};

export default ResponsiveFilters;
